import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { loginUser, ErrorType } from '../api'; // Import the loginUser function and ErrorType

const Login = () => {
  const { loginUser: authLogin, user, isCheckingAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState(''); // State for error message
  const [successMessage, setSuccessMessage] = useState(''); // State for success message
  const [isLoading, setIsLoading] = useState(false);
  const verificationSender = 'no-reply@courtbookings.co.za';
  const renderMessageWithSenderHighlight = (message) => {
    if (!message || !message.includes(verificationSender)) {
      return message;
    }

    const [before, after] = message.split(verificationSender);
    return (
      <>
        {before}
        <span className="font-semibold break-all">{verificationSender}</span>
        {after}
      </>
    );
  };

  // Read app name from environment variable, default to 'Court Booking System' if not set
  const appName = process.env.REACT_APP_APP_NAME || 'Court Booking System';

  // Check for success message in location state or URL params
  useEffect(() => {
    if (location.state && location.state.message) {
      setSuccessMessage(location.state.message);
    }
    
    // Check for message in URL params (for session expired redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const message = urlParams.get('message');
    if (message) {
      setError(message);
      // Clear the message from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  // Redirect to dashboard if already authenticated (double-check in case PublicRoute didn't catch it)
  useEffect(() => {
    // Wait for auth check to complete before redirecting
    if (!isCheckingAuth && user) {
      const redirect = localStorage.getItem('postLoginRedirect');
      if (redirect && redirect.startsWith('/')) {
        localStorage.removeItem('postLoginRedirect');
        navigate(redirect, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [navigate, user, isCheckingAuth]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(''); // Clear error when user starts typing
  };

  const validateForm = () => {
    if (!form.email || !form.password) {
      setError('All fields are required');
      return false;
    }
    // Basic email validation - trim email before validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear error message
    setSuccessMessage(''); // Clear success message

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const token = await loginUser(form);
      authLogin(token);
      const redirect = localStorage.getItem('postLoginRedirect');
      if (redirect && redirect.startsWith('/')) {
        localStorage.removeItem('postLoginRedirect');
        navigate(redirect, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      // Handle different error types appropriately
      switch (err.type) {
        case ErrorType.AUTHENTICATION:
          setError(err.message);
          break;
        case ErrorType.NETWORK:
          setError('Unable to connect to the server. Please check your internet connection and try again.');
          break;
        case ErrorType.SERVER:
          setError('The server encountered an error. Please try again later.');
          break;
        default:
          setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
        <div className="relative flex items-center mb-6">
          <h2 className="w-full text-center text-2xl font-bold text-slate-800">{appName}</h2>
        </div>
        <p className="text-slate-600 text-center mb-6">Please sign in to continue</p>
        
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
            {renderMessageWithSenderHighlight(successMessage)}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            {renderMessageWithSenderHighlight(error)}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-600 mb-1">
              Email Address
            </label>
            <input
              id="email"
              type="text"
              name="email"
              autoComplete="email"
              className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-600 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              name="password"
              autoComplete="current-password"
              className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
              placeholder="••••••••"
              value={form.password}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 px-4 rounded-md text-white font-medium transition-all duration-200 ${
              isLoading 
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            } flex items-center justify-center`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-slate-600">
            Trouble logging in?{' '}
          <Link 
            to="/reset-password" 
            className="font-medium text-blue-600 hover:text-blue-500 transition"
          >
            Reset password
          </Link>
        </p>

        <p className="mt-4 text-center text-sm text-slate-600">
          Don't have an account?{' '}
          <Link 
            to="/register" 
            className="font-medium text-blue-600 hover:text-blue-500 transition"
          >
            Create one now
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
